// CheerpX mount table for a machine. Mirrors WebVM's device stack (EndisOS verified it on 1.3.9):
//   /           ext2  OverlayDevice(base image, IDBDevice "revault-cx-blocks-<id>")  (persistent disk)
//   /data       dir   DataDevice                         (files sent from Re:Vault; read-only in the guest)
//   /files      dir   IDBDevice "revault-cx-files-<id>"  (guest -> Re:Vault exports: /files/outbox)
//   /dev /dev/pts /proc /sys                              (kernel pseudo devices)
// The runtime names each IDBDevice's database "cjFS_/<device>/" - taken from the runtime, not docs
// (EndisOS's reset once erased the wrong database by guessing).
import { LinuxError } from "../engine.js";
import { awaitOperation } from "./operation.js";
import { deleteDatabase, cheerpxDbName } from "../../shared/linux-store.js";
import { MACHINE_ID_RE } from "../lock.js";
import { cheerpxImage, imageKey } from "../../shared/linux-catalog.js";

export const blocksDevice = (id) => "revault-cx-blocks-" + id;
export const filesDevice = (id) => "revault-cx-files-" + id;
export function cheerpOsDbName(device) {
	if (!/^revault-cx-(blocks|files)-[a-z0-9][a-z0-9-]{0,63}$/.test(device)) throw new Error("Invalid Linux device name");
	return cheerpxDbName(device);
}

// wss:// -> CloudDevice (Leaning's disk service); https:// -> HttpBytesDevice (needs Range + CORS).
// "rv:<id>" is a disk Re:Vault serves itself (CHEERPX_IMAGES), resolved against THIS origin and its
// opaque Linux prefix, so a machine saved on one Re:Vault domain still works on another.
// Plain http:// is accepted only for the page's own origin (a local dev server).
// build.mjs defines each `__RVM.<key>` separately (there is no `__RVM` object), so read the property
// itself; outside a bundle (Node tests) the bare identifier throws and the prefix is empty.
const linuxPrefix = () => { try { return __RVM.linux || ""; } catch (e) { return ""; } };
export function classifyImageUrl(imageUrl, pageUrl, imagePrefix = linuxPrefix()) {
	let raw = String(imageUrl || "").trim();
	if (!raw) return { ok: false, reason: "This machine has no disk image." };
	if (raw.startsWith("rv:")) {
		const img = cheerpxImage(raw.slice(3));
		if (!img) return { ok: false, reason: "This machine's disk isn't offered by this version of Re:Vault." };
		if (!/^\/[A-Za-z0-9]+\/$/.test(imagePrefix)) return { ok: false, reason: "This Re:Vault server needs a rebuild before this machine can run (missing disk route)." };
		try { raw = new URL(imagePrefix + "i/" + imageKey(img), pageUrl).href; } catch (e) { return { ok: false, reason: "The disk image URL isn't valid." }; }
		const u = new URL(raw);
		return { ok: true, kind: "http", url: raw, local: img, statusUrl: u.origin + imagePrefix + "s/" + imageKey(img) };
	}
	let url;
	try { url = new URL(raw, pageUrl); } catch (e) { return { ok: false, reason: "The disk image URL isn't valid." }; }
	if (url.username || url.password) return { ok: false, reason: "The disk image URL must not contain credentials." };
	if (url.protocol === "wss:") return { ok: true, kind: "cloud", url: url.href };
	if (url.protocol === "https:") return { ok: true, kind: "http", url: url.href };
	if (url.protocol === "http:" && pageUrl && url.origin === new URL(pageUrl).origin) return { ok: true, kind: "http", url: url.href };
	return { ok: false, reason: "Unsupported disk image scheme \"" + url.protocol + "\" (use wss:// or https://)." };
}

// Pure description of the mount order ("/" first; every later path lives inside it).
export function mountPlan() {
	return [
		{ type: "ext2", path: "/", role: "root" },
		{ type: "dir", path: "/data", role: "data" },
		{ type: "dir", path: "/files", role: "files" },
		{ type: "devs", path: "/dev", role: null },
		{ type: "devpts", path: "/dev/pts", role: null },
		{ type: "proc", path: "/proc", role: null },
		{ type: "sys", path: "/sys", role: null },
	];
}

export async function createMountTable(rt, machine, { pageUrl, signal }) {
	const wait = (op) => { if (signal) signal.throwIfAborted(); return signal ? awaitOperation(op(), signal) : op(); };
	if (!MACHINE_ID_RE.test(machine.id)) throw new LinuxError("IMAGE_INVALID", "Machine id \"" + machine.id + "\" can't name a disk.");
	const image = classifyImageUrl(machine.image, pageUrl);
	if (!image.ok) throw new LinuxError("IMAGE_INVALID", image.reason);
	// A disk Re:Vault serves itself may not be installed on this server: say so plainly instead of
	// letting CheerpX fail on a 503.
	if (image.statusUrl) {
		let st = null;
		try { const r = await fetch(image.statusUrl, { cache: "no-store", signal }); st = r.ok ? await r.json() : null; } catch (e) { if (signal && signal.aborted) throw e; }
		if (!st || st.state !== "ready") throw new LinuxError("IMAGE_UNREACHABLE", "The " + image.local.name + " disk isn't available on this server yet. Try again later, or pick another machine.");
	}
	let base;
	try { base = await wait(() => (image.kind === "cloud" ? rt.CloudDevice.create(image.url) : rt.HttpBytesDevice.create(image.url))); }
	catch (err) {
		if (signal && signal.aborted) throw err;
		throw new LinuxError("IMAGE_UNREACHABLE", "The disk at " + new URL(image.url).host + " couldn't be opened (" + (err && err.message ? err.message : String(err)) + "). Some networks block it; try again later.", err);
	}
	let blocks, files, data, root;
	try {
		blocks = await wait(() => rt.IDBDevice.create(blocksDevice(machine.id)));
		if (!machine.persist) await wait(() => blocks.reset());
		root = await wait(() => rt.OverlayDevice.create(base, blocks));
		data = await wait(() => rt.DataDevice.create());
		files = await wait(() => rt.IDBDevice.create(filesDevice(machine.id)));
	} catch (err) {
		if (signal && signal.aborted) throw err;
		throw new LinuxError("STORAGE_FAILED", "Browser storage for the Linux disk couldn't be opened (" + (err && err.message ? err.message : String(err)) + ").", err);
	}
	const devs = { root, data, files };
	const mounts = mountPlan().map((e) => (e.role ? { type: e.type, path: e.path, dev: devs[e.role] } : { type: e.type, path: e.path }));
	return { mounts, base, blocks, root, data, files, imageKind: image.kind, imageUrl: image.url };
}

// Deletes a machine's CheerpX databases with no runtime loaded (fresh document only; see reset).
export async function deleteMachineDisks(id, del = deleteDatabase) {
	const results = await Promise.all([del(cheerpOsDbName(blocksDevice(id))), del(cheerpOsDbName(filesDevice(id)))]);
	return results.every(Boolean);
}
